import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Alert, AlertTitle, Backdrop, Box, Card, CardContent, CardHeader, CircularProgress, Fade, Grid, Modal, Typography } from '@mui/material';
import Papa from 'papaparse';
import React, { useEffect, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import { WO2Checkbox } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { Props } from '../../container';
import { Props as SchedulerProps } from '../../scheduler/container';
import { ClientCsvRow, ClientSearchResult } from '../../store/types';

interface CsvPropsBase {
  handleClose: () => void;
  handleAppendClientsFromCsv: (clients: ClientSearchResult[], allowTerminatedClients: boolean) => void;
  isOpen: boolean;
}

interface CsvPropsBulk extends CsvPropsBase, Props {}

interface CsvPropsScheduler extends CsvPropsBase, SchedulerProps {}

const formatFileSize = (bytes: number): string => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`);

export const ReportingGenerationClientCsvModal = (props: CsvPropsBulk | CsvPropsScheduler): JSX.Element => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [rows, setRows] = useState<ClientSearchResult[]>([]);
  const [hasAcknowledgedTerminatedClients, setHasAcknowledgedTerminatedClients] = useState<boolean>(false);
  const csvRows = useRef<ClientCsvRow[]>([]);

  const selectedFile = acceptedFiles[0];
  const isValidating = props.validateClientIdsProgress.isLoading;

  const handleValidFile = async (files: File[]) => {
    const error = files[0].name.indexOf('.csv') < 0;
    setFileError(error ? 'Only CSV files can be uploaded.' : '');
    setImportErrors([]);
    setRows([]);
    setAcceptedFiles(files);
    csvRows.current = [];

    if (error) {
      return;
    }

    Papa.parse(files[0], {
      header: true,
      skipEmptyLines: true,
      complete: function (results: Papa.ParseResult<ClientCsvRow>) {
        // validate imported clientId/ groupId list
        const rowIds = results.data
          .map((row) => String(row.ID ?? '').trim())
          .filter((id) => /^\d+$/.test(id))
          .map(Number);

        csvRows.current = results.data;
        props.validateClientIds({ clientIds: rowIds });
      },
    });
  };

  useEffect(() => {
    if (csvRows.current.length === 0) {
      return;
    }

    const validClients: ClientSearchResult[] = [];
    const errors: string[] = [];

    csvRows.current.forEach((row) => {
      const id = String(row.ID ?? '').trim();
      const validated = props.bulk.validatedClients.find((result) => Number(id) === result.clientId);

      if (validated?.isAuthorised) {
        validClients.push({
          clientId: validated.clientId,
          clientType: validated.isGroup ? 'Group' : 'Client',
          clientName: validated.clientName ?? `Client (${validated.clientId})`,
        });
      } else {
        errors.push(`"${id}" is not a valid client or group ID, or you don't have access to it.`);
      }
    });

    setImportErrors(errors);
    setRows(validClients);

    if (props.bulk.validatedClients.some((client) => client.status && client.status !== 'Active')) {
      setHasAcknowledgedTerminatedClients(true);
    }
  }, [props.bulk.validatedClients]);

  const completeImportCsv = () => {
    props.handleAppendClientsFromCsv(rows, hasAcknowledgedTerminatedClients);
    props.handleClose();
  };

  useEffect(() => {
    csvRows.current = [];
    setAcceptedFiles([]);
    setFileError('');
    setImportErrors([]);
    setRows([]);
  }, [props.isOpen]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={() => {
        props.handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CardHeader
            title={<Typography variant="h3">Import clients and groups</Typography>}
            subheader="Upload a CSV file with an ID column to add clients and groups in bulk."
            subheaderTypographyProps={{ sx: { mt: 1 } }}
          />
          <CardContent>
            <Grid container spacing={2} direction="column">
              {fileError && (
                <Grid item>
                  <Alert variant="outlined" severity="error">
                    {fileError}
                  </Alert>
                </Grid>
              )}
              {importErrors.length > 0 && (
                <Grid item>
                  <Alert variant="outlined" severity="error">
                    <AlertTitle>
                      {importErrors.length} {importErrors.length === 1 ? 'row was' : 'rows were'} skipped and will not be imported
                    </AlertTitle>
                    <Box maxHeight={150} overflow="auto">
                      {importErrors.map((error, index) => (
                        <Typography key={index} variant="body2">
                          {error}
                        </Typography>
                      ))}
                    </Box>
                  </Alert>
                </Grid>
              )}
              <Grid item>
                <Dropzone onDrop={(files) => handleValidFile(files)} multiple={false} disabled={isValidating}>
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <Box
                      data-testid="dropzone"
                      {...getRootProps()}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        p: 4,
                        textAlign: 'center',
                        cursor: isValidating ? 'progress' : 'pointer',
                        outline: 'none',
                        border: '2px dashed',
                        borderRadius: 2,
                        borderColor: isDragActive ? 'primary.main' : 'divider',
                        bgcolor: isDragActive ? 'action.hover' : 'background.default',
                        transition: 'border-color 0.2s, background-color 0.2s',
                        '&:hover, &:focus-visible': { borderColor: 'primary.main' },
                      }}
                    >
                      <input
                        {...getInputProps({
                          accept: '.csv',
                          type: 'file',
                        })}
                      />
                      {selectedFile ? (
                        <>
                          {isValidating ? <CircularProgress size={35} /> : <InsertDriveFileOutlinedIcon color="primary" fontSize="large" />}
                          <Typography variant="body1" fontWeight={600}>
                            {selectedFile.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {isValidating ? 'Validating IDs…' : `${formatFileSize(selectedFile.size)} · Drop or click to replace`}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CloudUploadOutlinedIcon color="action" fontSize="large" />
                          <Typography variant="body1">{isDragActive ? 'Drop the file here' : 'Drag and drop a CSV file here'}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            or click to browse
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                </Dropzone>
              </Grid>
              <Grid item>
                <WO2Checkbox
                  label="Include non-active clients - CSV only"
                  onChangeHandler={(value: boolean) => {
                    setHasAcknowledgedTerminatedClients(value);
                  }}
                  checked={hasAcknowledgedTerminatedClients}
                />
              </Grid>
              <Grid item container spacing={2} mt={2} justifyContent="space-between">
                <Grid item>
                  <WO2Button variant="outlined" onClick={props.handleClose}>
                    Cancel
                  </WO2Button>
                </Grid>
                {acceptedFiles && acceptedFiles.length > 0 && rows.length > 0 && (
                  <Grid item>
                    <WO2Button variant="contained" onClick={completeImportCsv}>
                      Import
                    </WO2Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>
    </Modal>
  );
};
